import { useRequestOTP, useUser, useVerifyOTP, type OTPType } from '@/api';
import {
  Button,
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '@/components/atoms';
import { getAuthToken, getUserIdFromAuthToken } from '@/lib/auth';
import {
  useEffect,
  useState,
  type Dispatch,
  type FormEvent,
  type SetStateAction,
} from 'react';
import { useOutletContext } from 'react-router';
import Countdown from 'react-countdown';
import toast from 'react-hot-toast';
import type { OutletContext } from '@/components/layouts';

interface OTPFormProps {
  identifier?: string;
  type?: OTPType;
  setOtpCode?: Dispatch<SetStateAction<string | undefined>>;
  setUserId?: Dispatch<SetStateAction<number | undefined>>;
}

export const OTPForm = ({
  identifier: identifierProp,
  type = 'forgot-password',
  setOtpCode,
  setUserId,
}: OTPFormProps) => {
  const [code, setCode] = useState('');
  const [resolvedIdentifier, setResolvedIdentifier] = useState(
    identifierProp?.trim() ?? ''
  );
  const [duration, setDuration] = useState(Date.now() + 120 * 1000);
  const [key, setKey] = useState(0);

  const { incrementStep, goToStep } = useOutletContext<OutletContext>();

  const { mutate: request } = useRequestOTP();
  const { mutate: verify, isPending } = useVerifyOTP();

  // login/google may land here without a phone in route state — load from /users/me.
  const needsMeLookup =
    type === 'phone-verification' &&
    !resolvedIdentifier &&
    Boolean(getAuthToken());
  const { data: me } = useUser(needsMeLookup ? 'me' : undefined);

  useEffect(() => {
    const fromProp = identifierProp?.trim();
    if (fromProp) {
      setResolvedIdentifier(fromProp);
      return;
    }
    if (me?.whatsappNumber?.trim()) {
      setResolvedIdentifier(me.whatsappNumber.trim());
    }
  }, [identifierProp, me]);

  useEffect(() => {
    if (me?.id != null) {
      setUserId?.(me.id);
    }
  }, [me, setUserId]);

  const identifier = resolvedIdentifier;
  const isCodeComplete = code.replace(/\D/g, '').length === 5;
  const canVerify = isCodeComplete && !isPending;

  const handleVerified = (verifiedCode: string) => {
    if (type === 'forgot-password') {
      setOtpCode?.(verifiedCode);
      incrementStep();
      return;
    }

    if (type === 'phone-verification') {
      const userId = getUserIdFromAuthToken() ?? me?.id;
      setUserId?.(userId);
      goToStep(3, {
        userId,
        identifier,
      });
    }
  };

  const submitVerification = (otpCode: string) => {
    const digits = otpCode.replace(/\D/g, '');
    if (digits.length !== 5) return;

    if (!identifier) {
      toast.error(
        'Phone number is still loading. Please try again in a moment.'
      );
      return;
    }

    verify(
      { code: digits, identifier, type },
      {
        onSuccess: () => handleVerified(digits),
      }
    );
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!canVerify) return;
    submitVerification(code);
  };

  const handleOTPResend = () => {
    setDuration(Date.now() + 120 * 1000);
    setKey((prev) => prev + 1);
    if (identifier) {
      request({
        identifier,
        type:
          type === 'phone-verification'
            ? 'phone-verification'
            : 'forgot-password',
        channel: type === 'phone-verification' ? 'whatsapp' : 'email',
      });
    }
  };

  // Ensure a WhatsApp PIN is outstanding when this step mounts (e.g. Google signup).
  useEffect(() => {
    if (type !== 'phone-verification' || !identifier) return;
    request({
      identifier,
      type: 'phone-verification',
      channel: 'whatsapp',
    });
    // Intentionally once per identifier mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identifier, type]);

  const renderer = ({
    minutes,
    seconds,
    completed,
  }: {
    minutes: number;
    seconds: number;
    completed: boolean;
  }) => {
    if (completed) {
      return (
        <span
          className="text-primary cursor-pointer"
          role="button"
          onClick={handleOTPResend}
        >
          Resend OTP
        </span>
      );
    }

    return (
      <span className="text-primary/50">
        Resend in {minutes}m {seconds}secs
      </span>
    );
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="grid grid-cols-2 gap-4 px-4 md:px-6 lg:px-8"
    >
      <InputOTP
        maxLength={5}
        inputMode="numeric"
        pattern="[0-9]*"
        value={code}
        onChange={(value) => setCode(value.replace(/\D/g, '').slice(0, 5))}
      >
        <InputOTPGroup>
          {Array.from({ length: 5 }).map((_, i) => (
            <InputOTPSlot key={i} index={i} />
          ))}
        </InputOTPGroup>
      </InputOTP>
      <Button
        type="submit"
        className="col-span-2"
        label="Continue"
        size="lg"
        isLoading={isPending}
        disabled={!canVerify}
      />
      <div className="flex items-center justify-between gap-2 text-sm text-muted-foreground col-span-2">
        <span>Didn't get the code?</span>
        <Countdown key={key} date={duration} renderer={renderer} />
      </div>
    </form>
  );
};
